/*
Component for creating flexible grid that where amount of grid columns
adjust to fit as many columns without column width going below minWidth.
(exactly like 'display grid' with 'auto-fill').
*/

import { useState, CSSProperties, ReactNode, useRef } from 'react';
import cn from 'classnames';

import useResize from 'js/hooks/use-resize';

import AutoGridItem from './AutoGridItem';

interface GridStyles {
  gridStyle: CSSProperties;
  itemStyle: CSSProperties;
}

const calcFlexibleGutterStyle = (minWidth: number, gridWidth: number): GridStyles => {
  if (!gridWidth) return { gridStyle: {}, itemStyle: {} };

  const gaps = Math.floor(gridWidth / (minWidth + 1)) - 1;
  let gapLength = gaps === 0 ? 24 : Math.floor((gridWidth % (minWidth + 1)) / gaps);
  const itemWidth = gaps === 0 ? gridWidth : minWidth;

  const extraPxs = gridWidth - (gaps * gapLength + itemWidth * (gaps + 1));
  if (extraPxs > gaps) gapLength++;

  const gridStyle: CSSProperties = {
    gridTemplateColumns: `repeat(auto-fill, ${itemWidth}px)`,
    gap: gapLength
  };

  return { gridStyle, itemStyle: {} };
};

interface AutoGridLogicReturn {
  autoGridProps: {
    style: CSSProperties;
    ref: (node: HTMLElement | null) => void;
  };
  itemStyle: CSSProperties;
}

const AutoGridLogic = (minWidth: number): AutoGridLogicReturn => {
  const [gridWidth, setGridWidth] = useState(0);
  const nodeRef = useRef<HTMLElement | null>(null);
  const { gridStyle, itemStyle } = calcFlexibleGutterStyle(minWidth, gridWidth);

  const setWidth = (node: HTMLElement): void => {
    const width = node.offsetWidth;
    if (width !== gridWidth) {
      setGridWidth(width);
    }
  };

  useResize(() => {
    const node = nodeRef.current;
    if (node) setWidth(node);
  });

  const handleRef = (n: HTMLElement | null): void => {
    nodeRef.current = n;
    if (n) setWidth(n);
  };

  return {
    autoGridProps: {
      style: gridStyle,
      ref: handleRef
    },
    itemStyle
  };
};

interface AutoGridProps {
  children?: (ItemComponent: ReturnType<typeof AutoGridItem>) => ReactNode;
  isList?: boolean;
  minWidth?: number;
  flexibleGutter?: boolean;
  className?: string;
}

const AutoGrid: React.FC<AutoGridProps> = ({
  children = () => {},
  isList,
  minWidth = 250,
  flexibleGutter,
  className
}) => {
  let effectiveMinWidth: number | null = minWidth;

  if (isList) {
    effectiveMinWidth = null;
  }

  let autoGridProps: { style: CSSProperties; ref?: (node: HTMLElement | null) => void };
  let itemStyle: CSSProperties = {};

  if (flexibleGutter && effectiveMinWidth) {
    const logic = AutoGridLogic(effectiveMinWidth);
    autoGridProps = logic.autoGridProps;
    itemStyle = logic.itemStyle;
  } else {
    autoGridProps = {
      style: {
        gridTemplateColumns: effectiveMinWidth ? `repeat(auto-fill, minmax(${effectiveMinWidth}px, 1fr))` : undefined
      }
    };
  }

  const ItemComponent = AutoGridItem(itemStyle);

  return (
    <ul className={cn('auto-grid', { 'auto-grid--theme-grid': effectiveMinWidth }, className)} {...autoGridProps}>
      {children(ItemComponent) || null}
    </ul>
  );
};

AutoGrid.displayName = 'AutoGrid';

export default AutoGrid;
